import {observable, action, computed, asReference, extendObservable, transaction} from 'mobx';
import {createTransformer} from 'mobx-utils';
import {async} from '../../server-wait';
import autobind from 'autobind-decorator';
import QS from 'querystring';
import objectAssignDeep from 'utils/object-assign-deep';

import makeAbortable from 'abortable-promise';
import setFunctionName from 'utils/setFunctionName';

function transformer(target, prop, descriptor) {
  descriptor.value = createTransformer(descriptor.value);
}

export class Model {
  constructor(modelState) {
    Object.defineProperty(this, 'modelState', {
      value: modelState,
      enumerable: false,
    });
  }
}

export class Search {
  // key = null;
  // search = {};

  @observable isLoading = false;
  @observable error = null;
  // @private
  @observable keys = [];

  @observable pagination = {};

  abort() {}

  @computed get count() {
    return this.keys.length;
  }

  @computed get instances() {
    return observable(this.keys
      .map(this.modelState.getOneInstance)
      .filter(Boolean));
  }

  @action maybeLoad() {
    if (this.isLoading || !this.error) {
      return undefined;
    }
    return this.load();
  }
  @async @action load(...params) {
    this.abort();

    this.isLoading = true;
    this.error = null;

    const {promise, abort} = makeAbortable(this.modelState.getSearchRequest(this, ...params));

    this.abort = abort;

    return promise
      .then(this.onSuccess, this.onFailure);
  }

  // This one has fixed the error callback
  @async @action loadNew(...params) {
    this.abort();

    this.isLoading = true;
    this.error = null;

    const {promise, abort} = makeAbortable(this.modelState.getSearchRequest(this, ...params));

    this.abort = abort;

    return promise
      .then(this.onSuccess, this.onError);
  }

  @autobind @action onSuccess({response, json}) {
    this.isLoading = false;
    transaction(() => {
      let instances = json[this.modelState.plural] || [];
      if (this.modelState.iteratorMap && this.modelState.iteratorMap[this.modelState.plural]) {
        instances = instances[this.modelState.iteratorMap[this.modelState.plural]];
      }
      if (typeof this.modelState.extra === 'string') {
        this.modelState[this.modelState.extra] = json[this.modelState.extra];
      } else if (Array.isArray(this.modelState.extra)) {
        for (const extra of this.modelState.extra) {
          this.modelState[extra] = json[extra];
        }
      }

      extendObservable(this.pagination, {
        total: instances && instances.length,
        pages: 1,
      });

      // Pagination - total number of results
      if (json.total) {
        this.pagination.total = json.total;
      }

      for (const instance of instances) {
        this.modelState.setOneInstance(instance);
      }

      this.keys.splice(
        0,
        this.keys.length,
        ...instances.map(instance =>
          this.modelState.getModelKey(instance.id)),
      );
    });
  }

  @autobind @action onError(error) {
    this.isLoading = false;
    this.error = error.json;

    throw this.error;
  }

  constructor(modelState, key, search) {
    Object.defineProperty(this, 'modelState', {
      value: modelState,
      enumerable: false,
    });
    Object.defineProperty(this, 'key', {
      value: key,
      readonly: true,
      enumerable: true,
    });
    Object.defineProperty(this, 'search', {
      value: search,
      readonly: true,
      enumerable: true,
    });
  }
}

export class ModelWrapper {
  // id = null;

  @observable isLoading = false;
  @observable error = null;
  @observable instance = null;

  abort() {}

  @async @action load() {
    this.abort();

    this.isLoading = true;
    this.error = null;

    const {promise, abort} = makeAbortable(this.modelState.getSingleRequest(this));

    this.abort = abort;

    return promise
      .then(this.onSuccess, this.onError);
  }

  @autobind @action onSuccess({response, json}) {
    this.isLoading = false;
    if (!json[this.modelState.singular]) {
      this.setInstance(json, true);
    }
    this.setInstance(json[this.modelState.singular], true);
  }
  @autobind @action onError(error) {
    this.isLoading = false;
    this.error = error.json;

    throw this.error;
  }

  setInstance(instance, noAbort = false) {
    if (!noAbort) { this.abort(); }
    transaction(() => {
      if (!this.instance) {
        this.instance = new this.modelState.Model(this.modelState);
      }
      const temp = {...instance};
      if (this.modelState.Model.relationships) {
        for (const relationship of this.modelState.Model.relationships) {
          this.instance[relationship.propName] = temp[relationship.propName];
          delete temp[relationship.propName];
        }
      }
      extendObservable(this.instance, temp);
      this.isLoading = false;
      this.error = null;
    });
  }

  constructor(modelState, id) {
    Object.defineProperty(this, 'modelState', {
      value: modelState,
      enumerable: false,
    });
    Object.defineProperty(this, 'id', {
      value: id,
      readonly: true,
      enumerable: true,
    });
  }
}

class ModelState {
  // url = '/models';
  // singular = 'model';
  // plural = 'models';

  // @private
  @transformer
  getSearchKey(search) { return JSON.stringify(search); }

  sanitizeModelId(id) { return `${id}`; }

  // @private
  getModelKey(id) { return this.sanitizeModelId(id); }

  /**
   * 'field=value&field2=value2' = Search {
   *   isLoading: false,
   *   abort()
   *   error: null,
   *   keys: [1, 2, 3],
   *   search: {field: 'value', field2: 'value2'},
   *   @computed instances: [{}, {}, {}],
   * }
   */
  @observable searches = observable.map();

  /**
   * 1 = ModelWrapper {
   *   isLoading: false,
   *   abort()
   *   error: null,
   *   instance: {},
   * }
   */
  @observable models = observable.map();

  @autobind @action maybeLoadSearch(search, ...params) {
    const id = this.getSearchKey(search);

    if (this.searches.has(id)) {
      return this.searches.get(id).maybeLoad();
    }
    return this.loadSearch(search, ...params);
  }
  @autobind @action loadSearch(search, ...params) {
    const id = this.getSearchKey(search);
    if (!this.searches.has(id)) {
      this.searches.set(id, new this.Search(this, id, search));
    }
    return this.searches.get(id).load(...params);
  }
  @action onSuccessSearch(id, response) {
    const search = this.searches.get(id);
    search.isLoading = false;
    search.response = observable(asReference(response));
  }
  @action onErrorSearch(id, error) {
    const search = this.searches.get(id);
    search.isLoading = false;
    search.error = error;
  }

  @autobind @action maybeLoadOne(id) {
    const key = this.getModelKey(id);
    if (this.models.has(key)) {
      const model = this.models.get(key);
      if (model.isLoading || !model.error) {
        return undefined;
      }
    }
    return this.loadOne(id);
  }
  @autobind @action loadOne(id) {
    const key = this.getModelKey(id);
    if (!this.models.has(key)) {
      this.models.set(key, new this.ModelWrapper(this, id));
    }

    return this.models.get(key).load();
  }
  @action onSuccessOne(id, response) {
    const key = this.getModelKey(id);
    const model = this.models.get(key);
    model.isLoading = false;
    model.response = observable(asReference(response));
  }
  @action onErrorOne(id, error) {
    const key = this.getModelKey(id);
    const model = this.models.get(key);
    model.isLoading = false;
    model.error = error;
  }

  @autobind getSearch(search) {
    const id = this.getSearchKey(search);
    return this.searches.get(id);
  }
  @autobind @action setOneInstance(instance) {
    let {id} = instance;
    id = this.sanitizeModelId(id);
    const key = this.getModelKey(id);
    let modelObject;
    if (!this.models.has(key)) {
      modelObject = new this.ModelWrapper(this, id);
      this.models.set(key, modelObject);
    } else {
      modelObject = this.models.get(key);
      modelObject.abort();
    }
    modelObject.setInstance(instance);
  }
  @autobind getOneInstance(id) {
    const key = this.getModelKey(id);
    return this.models.get(key) && this.models.get(key).instance;
  }

  getOne(id) {
    const key = this.getModelKey(id);
    return this.models.get(key);
  }

  getSearchRequest(search) {
    if (!this.url) {
      throw new Error('Missing option `url`');
    }
    if (!this.plural) {
      throw new Error('Missing option `plural`');
    }
    return this.ctx.sdk.get(`${this.url}?${QS.stringify(search.search)}`);
  }

  getSingleRequest(model) {
    if (!this.url) {
      throw new Error('Missing option `url`');
    }
    if (!this.singular) {
      throw new Error('Missing option `singular`');
    }

    if (this.defaultQuery) {
      return this.ctx.sdk.get(`${this.url}/${model.id}?${this.defaultQuery}`);
    }

    return this.ctx.sdk.get(`${this.url}/${model.id}`);
  }

  @action deserialize(value) {
    const {models, searches, ...rest} = value;
    objectAssignDeep(this, rest);
    if (models) {
      for (const _key of Object.keys(models)) {
        const object = models[_key];
        const id = this.sanitizeModelId(object.id);
        const key = this.getModelKey(id);
        const model = new this.ModelWrapper(this, id);
        model.isLoading = object.isLoading;
        model.error = object.error;
        model.setInstance(object.instance);
        this.models.set(key, model);
      }
    }
    if (searches) {
      for (const key of Object.keys(searches)) {
        const object = searches[key];
        const search = new this.Search(this, object.key, object.search);
        search.isLoading = object.isLoading;
        search.error = object.error;
        search.pagination = object.pagination;
        search.keys = object.keys;
        this.searches.set(object.key, search);
      }
    }
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    const className = this.singular
      .replace(/(^|[^a-z])[a-z]/mg, matches => matches[0].toUpperCase());

    if (!this.Model) {
      Object.defineProperty(this, 'Model', {
        value: class extends Model {},
        readonly: true,
        enumerable: false,
      });
      setFunctionName(this.Model, `${className}Model`);
    }
    if (!this.ModelWrapper) {
      Object.defineProperty(this, 'ModelWrapper', {
        value: class extends ModelWrapper {},
        readonly: true,
        enumerable: false,
      });
      setFunctionName(this.ModelWrapper, `${className}ModelWrapper`);
    }
    if (!this.Search) {
      Object.defineProperty(this, 'Search', {
        value: class extends Search {},
        readonly: true,
        enumerable: false,
      });
      setFunctionName(this.Search, `${className}Search`);
    }
  }
}

export default ModelState;

export function related(idPropName, multi = false, ctxKey) {
  if (typeof idPropName === 'function') {
    throw new Error('Incorrect use of @related');
  }
  return (ModelClass, propName) => {
    ModelClass.relationships = ModelClass.relationships || [];
    ctxKey = ctxKey || propName;
    const descriptor = {
      enumerable: false,
      get() {
        if (multi) {
          return this[idPropName].map(this.modelState.ctx[ctxKey].getOneInstance);
        }
        return this.modelState.ctx[ctxKey].getOneInstance(this[idPropName]);
      },
      set(value) {
        if (value == null) {
          this[idPropName] = null;
          return;
        }
        if (multi) {
          this[idPropName] = value.map((model) => {
            this.modelState.ctx[ctxKey].setOneInstance(model);
            return model.id;
          });
        } else {
          this.modelState.ctx[ctxKey].setOneInstance(value);
          this[idPropName] = value.id;
        }
      },
    };

    ModelClass.relationships.push({
      propName,
      idPropName,
      multi,
      ctxKey,
    });

    return computed(ModelClass, propName, descriptor) || descriptor;
  };
}

export function localised(Klass) {
  // this logic seems counterintuitive but breaks the whole model state for localised models if eslint 'fixes' it
  if (!Klass instanceof ModelState) throw new Error('@localised can only be used with ModelState subclasses');// eslint-disable-line
  const {getSearchKey, getModelKey} = Klass.prototype;
  Object.defineProperty(Klass.prototype, 'getSearchKey', {
    enumerable: false,
    value(search) {
      return `${getSearchKey.call(this, search)}::${this.ctx.i18n.lc}`;
    },
  });
  Object.defineProperty(Klass.prototype, 'getModelKey', {
    enumerable: false,
    value(id) {
      return `${getModelKey.call(this, id)}::${this.ctx.i18n.lc}`;
    },
  });
  Object.defineProperty(Klass, 'isLocalized', {
    value: true,
    enumerable: false,
  });
}



// WEBPACK FOOTER //
// ./spa/state/_util/model.jsx